import {NativeApi, OS} from "@/modules/NativeBridge";
import {AbortableDelay} from "@/modules/delay";

export interface AppReviewTriggerApi {
    isUserAppReviewPending(os: OS): Promise<boolean>;
    userAppReviewTriggered(os: OS, triggerPoint: number): Promise<void>;
}

const noOpCanceller = () => {};

export default class AppReviewTrigger {
    private nativeApi: NativeApi;
    private api: AppReviewTriggerApi;


    constructor(nativeApi: NativeApi, api: AppReviewTriggerApi) {
        this.nativeApi = nativeApi;
        this.api = api;
    }

    private delayer = new AbortableDelay();
    private canceller: () => void = noOpCanceller;

    readonly delayTime = 2000; // Hope two seconds is suitable lingering time before showing the prompt

    // The first 200ms a "activity" event will not cancel the trigger.
    // This is so that the same event that triggered the review trigger run does not also cancel it.
    readonly preventCancellationMillis = 200;

    /**
     * Start waiting for specified time before maybe triggering the native user app review flow.
     * The trigger is cancelled if there is any user activity before the time is up.
     */
    async run(triggerPoint: number) {
        if(this.isPossible()) {
            // Cleanup previous runs:
            this.cancel();

            const delayPromise = this.delayer.delay(this.delayTime, false);
            // Use a separate closure to capture this for the event handler and store it in a class property to be able to remove it.
            this.canceller = () => {
                if (this.delayer.delayedMillis > this.preventCancellationMillis) {
                    this.cancel();
                }
            };
            document.body.addEventListener("click", this.canceller);
            document.body.addEventListener("touchstart", this.canceller);
            document.body.addEventListener("keydown", this.canceller);
            document.body.addEventListener("scroll", this.canceller);
            if (await delayPromise) {
                await this.maybeRequestUserReview(triggerPoint);
            }
        }
    }

    cancel() {
        this.delayer.abort();
        if(this.canceller != noOpCanceller) {
            document.body.removeEventListener("click", this.canceller);
            document.body.removeEventListener("touchstart", this.canceller);
            document.body.removeEventListener("keydown", this.canceller);
            document.body.removeEventListener("scroll", this.canceller);
            this.canceller = noOpCanceller;
        }
    }

    private isPossible(): boolean {
        return this.nativeApi.available && this.nativeApi.atleastVersion(1, 2, 0);
    }

    private async maybeRequestUserReview(triggerPoint: number) {
        try { // We don't want the user review functionality to ever show any errors to the user. Just fail silently.
            const os = this.nativeApi.os;
            if (await this.api.isUserAppReviewPending(os)) {
              const probablyTriggered = await this.nativeApi.requestReview();
              if (probablyTriggered) {
                await this.api.userAppReviewTriggered(os, triggerPoint);
              } else {
                  console.warn("AppReview did not trigger");
              }
            }
        } catch (e) {
            console.info(`user app review request failed`, e);
        }
    }

}